/**
 * iter87.225 — NBA Series Predictor pick modal, sibling of
 * `MlbSeriesPickModal.jsx`. Play-In games never surface here (they're
 * single-elimination, not a "series") — they're excluded from the
 * bracket rounds `nba_engine.build_initial_bracket` feeds this modal.
 *
 * Each open series lets the player pick which team advances + the
 * exact deciding game (4/5/6/7 for every round — best-of-7 all the
 * way through, unlike MLB's shorter early rounds) — scoring: correct
 * team 6pts (+4 if the game is ALSO exact → 10), wrong team -2pts
 * (+2 partial credit if the game alone is exact → nets 0).
 *
 * iter87.315 (user-requested) — brought up to parity with
 * `MlbSeriesPickModal.jsx`'s iter87.299/87.311/87.315 redesign: three
 * sections (Open, always visible; Locked & Live, collapsible starting
 * OPEN; Completed, collapsible starting closed with read-only green/
 * red scoring feedback + series score) instead of the older single
 * "current round only, everything mixed together" list. See that
 * file's comments for the full rationale — identical logic here.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Info, AlertTriangle, Check, Lock, ChevronDown, Trophy, X } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { Basketball } from "@phosphor-icons/react";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";
import { missedPlayoffsCopy } from "@/lib/playoffField";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";

function SeedChip({ seed }) {
    return (
        <span className="inline-flex w-6 h-6 items-center justify-center rounded-lg bg-black border border-white/10 font-mono text-[10px] font-bold text-zinc-400 shrink-0">
            {seed ?? "–"}
        </span>
    );
}

// iter87.315 — sibling of MlbSeriesPickModal's identical helper.
function seriesRecord(s) {
    let aWins = 0, bWins = 0;
    (s.games || []).forEach((g) => {
        if (!g.played) return;
        if ((g.score_a ?? 0) > (g.score_b ?? 0)) aWins++;
        else if ((g.score_b ?? 0) > (g.score_a ?? 0)) bWins++;
    });
    return { aWins, bWins };
}

export default function NbaSeriesPickModal({ open, onClose, tournamentId, participantId, teams, existingPicks, onPicksChanged, tournament }) {
    const [bracket, setBracket] = useState(null);
    const [picks, setPicks] = useState(existingPicks || {});
    const [savingAll, setSavingAll] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [showLiveLocked, setShowLiveLocked] = useState(true);
    const [showCompleted, setShowCompleted] = useState(false);

    const teamFor = (id) => (teams || []).find((t) => t.id === id);
    const nameFor = (id) => teamFor(id)?.name || id || "TBD";
    const seedFor = (id) => teamFor(id)?.seed;

    // iter87.300 — same fix as `MlbSeriesPickModal.jsx` (see its
    // comment): only resync local `picks` from the `existingPicks`
    // prop when the modal OPENS, not on every background dashboard
    // refresh while it's already open, so a stray stale-snapshot
    // refresh can't clobber an in-progress pick out of the UI.
    const existingPicksRef = useRef(existingPicks);
    existingPicksRef.current = existingPicks;

    // iter87.311 — sibling of MlbSeriesPickModal's identical fix:
    // refetch the bracket (series lock status) every 45s while the
    // modal is open instead of only once at open-time, so a series
    // that locks mid-session is reflected without needing to reopen.
    useEffect(() => {
        if (!open || !tournamentId) return;
        const load = () => api.getNbaBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
        load();
        setPicks(existingPicksRef.current || {});
        const id = setInterval(load, 45000);
        return () => clearInterval(id);
    }, [open, tournamentId]);

    if (!open) return null;

    const rounds = bracket?.rounds || [];
    const allSeries = rounds.flatMap((r) =>
        (r.series || [])
            .filter((s) => s.status !== "tbd")
            .map((s) => ({ ...s, _roundKey: r.key, _roundLabel: r.label, _validGames: r.valid_games })));
    const notSetYet = !bracket || allSeries.length === 0;
    const openSeries = allSeries.filter((s) => s.status === "open");
    const liveSeries = allSeries.filter((s) => s.status === "locked");
    const completedSeries = allSeries.filter((s) => s.status === "final");
    const pickableSeries = openSeries;
    const incompleteCount = pickableSeries.filter((s) => {
        const p = picks[s.id] || {};
        return !p.team_id || !p.game_count;
    }).length;
    const byRound = (list) => list.reduce((acc, s) => {
        if (!acc[s._roundKey]) acc[s._roundKey] = { label: s._roundLabel, series: [] };
        acc[s._roundKey].series.push(s);
        return acc;
    }, {});
    const liveByRound = byRound(liveSeries);
    const completedByRound = byRound(completedSeries);

    // iter87.436 (user-reported — "every time a tile is selected, it
    // saves with a green toast, I wanted the Save button to actually
    // do the save") — tapping a team or game-count tile now only
    // updates LOCAL state. Nothing hits the backend, and no toast
    // fires, until "Save Picks" below is tapped. Previously every tile
    // tap fired its own save request + "Series pick saved" toast,
    // which made the tile itself feel like the save action — the
    // explicit "Save Picks" button sitting right below it was
    // redundant and confusing.
    const selectPick = (seriesId, teamId, gameCount) => {
        setPicks((prev) => ({ ...prev, [seriesId]: { team_id: teamId, game_count: gameCount ?? null } }));
    };

    // iter87.277 (user-requested), sibling of the MLB modal — explicit
    // final confirm: blocks with a clear error if any open series has
    // only a team OR only a game count picked.
    // iter87.436 (user-reported) — this is now the ONLY thing that
    // persists picks to the backend (see `selectPick` above) — submits
    // every open series' pick in one round trip.
    const onSavePicks = async () => {
        const pickable = pickableSeries;
        const incomplete = pickable.filter((s) => {
            const p = picks[s.id] || {};
            return !p.team_id || !p.game_count;
        });
        if (incomplete.length > 0) {
            toast.error(`Pick a team AND a game count for every series before saving — ${incomplete.length} series still incomplete`);
            return;
        }
        setSavingAll(true);
        try {
            await Promise.all(pickable.map((s) => {
                const p = picks[s.id];
                return api.submitNbaSeriesPick(tournamentId, participantId, s.id, p.team_id, p.game_count);
            }));
            onPicksChanged?.(picks);
            toast.success("All picks saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Some picks failed to save — try again");
        } finally {
            setSavingAll(false);
        }
    };

    // iter87.277 — "Clear All Picks": wipes open series only, locally + backend.
    const onClearAll = async () => {
        const pickable = pickableSeries;
        const idsWithPicks = pickable.filter((s) => picks[s.id]?.team_id || picks[s.id]?.game_count).map((s) => s.id);
        if (idsWithPicks.length === 0) {
            toast.info("No picks to clear yet");
            return;
        }
        setClearing(true);
        try {
            await api.clearNbaSeriesPicks(tournamentId, participantId, idsWithPicks);
            const next = { ...picks };
            idsWithPicks.forEach((id) => delete next[id]);
            setPicks(next);
            onPicksChanged?.(next);
            toast.success("Picks cleared");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't clear picks");
        } finally {
            setClearing(false);
        }
    };

    // iter87.315 — shared card renderer for Open, Locked & Live, and
    // Completed sections. See MlbSeriesPickModal's identical function
    // for the full rationale.
    const renderSeriesCard = (s) => {
        const pick = picks[s.id] || {};
        const locked = s.status !== "open";
        const isFinal = s.status === "final";
        const isLive = s.status === "locked";
        const record = (isFinal || isLive) ? seriesRecord(s) : null;
        const pickedWinner = isFinal && !!pick.team_id && pick.team_id === s.winner_team_id;
        const pickedLoser = isFinal && !!pick.team_id && pick.team_id !== s.winner_team_id;
        const outerBorderClass = pickedWinner ? "border-emerald-500" : pickedLoser ? "border-red-500" : "border-neutral-800";
        return (
            <div
                key={s.id}
                data-testid={`nba-series-${s.id}`}
                data-result={isFinal ? (pickedWinner ? "correct" : pickedLoser ? "incorrect" : "no-pick") : undefined}
                className={`brutal-card bg-neutral-900 border-2 ${outerBorderClass} p-4 space-y-3`}
            >
                <div className="flex items-center gap-2 flex-wrap">
                    {locked && <Lock className="w-3 h-3 text-zinc-500 shrink-0" data-testid={`nba-series-${s.id}-locked-icon`} />}
                    {s._roundLabel && (
                        <span className="inline-flex items-center rounded-lg bg-neutral-950 border border-white/10 text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider text-zinc-500">
                            {s._roundLabel}
                        </span>
                    )}
                    {s.league && (
                        <span className="inline-flex items-center rounded-lg bg-neutral-950 border border-white/10 text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider text-zinc-400">
                            {s.league}
                        </span>
                    )}
                    {record && (
                        <span data-testid={`nba-series-${s.id}-record`} className="ml-auto font-mono text-xs font-bold text-zinc-300 tabular-nums">
                            {record.aWins}–{record.bWins}
                        </span>
                    )}
                </div>
                {isFinal && (
                    <p data-testid={`nba-series-${s.id}-result`} className="text-[10px] uppercase tracking-widest text-zinc-400">
                        Final — <span className="text-cyber font-bold">{nameFor(s.winner_team_id)}</span> won in {s.deciding_game}
                    </p>
                )}
                {!isFinal && pick.team_id && pick.team_id !== s.team_a_id && pick.team_id !== s.team_b_id && (
                    <p data-testid={`nba-series-${s.id}-dropped-notice`} className="text-heat text-[10px] font-semibold flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        {missedPlayoffsCopy(nameFor(pick.team_id), tournament, { locked, subject: "your pick" })}
                    </p>
                )}
                <div>
                    {[[s.team_a_id, s.team_a_seed ?? seedFor(s.team_a_id)], [s.team_b_id, s.team_b_seed ?? seedFor(s.team_b_id)]].map(([tid, seed], idx) => {
                        const isSelected = pick.team_id === tid;
                        let rowClass = isSelected ? "border-volt bg-volt/20" : "border-transparent bg-neutral-800 hover:border-neutral-600";
                        if (isFinal && isSelected) {
                            rowClass = tid === s.winner_team_id ? "border-emerald-500 bg-emerald-500/10" : "border-red-500 bg-red-500/10";
                        }
                        return (
                            <button
                                key={tid || idx}
                                data-testid={`nba-series-${s.id}-pick-${idx === 0 ? "a" : "b"}`}
                                aria-pressed={isSelected}
                                disabled={locked || !tid}
                                onClick={() => selectPick(s.id, tid, pick.game_count)}
                                className={`flex items-center justify-between w-full p-3 mb-2 rounded-lg border-2 transition-all
                                    ${rowClass}
                                    ${locked && !isFinal ? "opacity-50 cursor-not-allowed" : locked ? "cursor-not-allowed" : ""}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <SeedChip seed={seed} />
                                    <TeamSwatch name={nameFor(tid)} sport="nba" size="w-6 h-6 text-[9px]" />
                                    <span className={`text-sm font-bold truncate text-left ${isSelected ? (isFinal ? (tid === s.winner_team_id ? "text-emerald-400" : "text-red-400") : "text-volt") : "text-white"}`}>{nameFor(tid)}</span>
                                </div>
                                {isSelected && <Check className={`w-4 h-4 shrink-0 ml-2 ${isFinal ? (tid === s.winner_team_id ? "text-emerald-400" : "text-red-400") : "text-volt"}`} />}
                            </button>
                        );
                    })}
                </div>
                <div>
                    <span className="text-xs text-neutral-500 font-mono mb-2 block uppercase tracking-widest">Games</span>
                    <div className="flex justify-between gap-2">
                        {(s._validGames || []).map((g) => {
                            const isPicked = pick.game_count === g;
                            let pillClass = isPicked
                                ? "bg-cyber text-black border-cyber font-bold"
                                : "border-neutral-700 bg-neutral-950 text-zinc-300 hover:border-neutral-500";
                            if (isFinal && isPicked) {
                                pillClass = s.deciding_game === g
                                    ? "bg-emerald-500/20 text-emerald-300 border-emerald-500 font-bold"
                                    : "bg-red-500/20 text-red-300 border-red-500 font-bold";
                            }
                            return (
                                <button
                                    key={g}
                                    data-testid={`nba-series-${s.id}-game-${g}`}
                                    aria-pressed={isPicked}
                                    disabled={locked || !pick.team_id}
                                    onClick={() => selectPick(s.id, pick.team_id, g)}
                                    className={`pill flex-1 py-2 text-center border-2 font-medium transition-colors
                                        ${pillClass}
                                        ${(locked || !pick.team_id) && !isFinal ? "opacity-40 cursor-not-allowed" : (locked || !pick.team_id) ? "cursor-not-allowed" : ""}`}
                                >
                                    in {g}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="nba-series-pick-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="brutal-card max-w-lg w-full max-h-[90dvh] overflow-y-auto border-volt"
        >
            <div className="sticky top-0 z-10 bg-[#121212] border-b-2 border-neutral-800 px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
                <h3 className="font-display font-extrabold uppercase text-lg flex items-center gap-2">
                    <Basketball size={18} weight="duotone" />
                    Series Predictor
                </h3>
                <button
                    onClick={onClose}
                    className="brutal-btn-ghost p-2 shrink-0"
                    data-testid="close-nba-series-pick-modal-btn"
                    aria-label="Close"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="space-y-4 p-4 sm:p-6">
                {!notSetYet && (
                    <div data-testid="nba-series-how-it-works" className="text-neutral-400 text-[11px] pb-4 border-b-2 border-neutral-800 flex gap-2 items-start text-left">
                        <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                        <p className="leading-relaxed">
                            <span className="text-white font-bold uppercase tracking-tight">How it works:</span> pick a winner + exact game
                            count for <span className="text-white font-semibold">every</span> series below.
                            Right team = <span className="text-cyber font-semibold">+6</span> (+4 more if the game
                            count is exact too). Wrong team = <span className="text-heat font-semibold">−2</span>
                            {" "}(+2 back if you still nailed the game count). Picks lock once that series' Game 1 starts.
                        </p>
                    </div>
                )}

                {notSetYet ? (
                    <p data-testid="nba-series-not-set-notice" className="text-xs text-zinc-500">
                        Playoff teams have not been determined yet — check back once {rounds[0]?.label || "First Round"} matchups are set.
                    </p>
                ) : (
                    <div className="space-y-4">
                        {openSeries.length > 0 ? (
                            <div className="space-y-3">
                                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold" data-testid="nba-series-open-heading">
                                    Open — Make Your Pick
                                </p>
                                {openSeries.map(renderSeriesCard)}
                            </div>
                        ) : (
                            <p data-testid="nba-series-no-open-notice" className="text-xs text-zinc-500">
                                No open series right now — check back once new matchups are set.
                            </p>
                        )}

                        <Collapsible open={showLiveLocked} onOpenChange={setShowLiveLocked}>
                            <CollapsibleTrigger
                                data-testid="nba-series-live-toggle"
                                className="flex items-center justify-between w-full py-2 border-t-2 border-neutral-800 text-left"
                            >
                                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5">
                                    <Lock className="w-3 h-3" /> Locked & Live ({liveSeries.length})
                                </span>
                                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showLiveLocked ? "rotate-180" : ""}`} />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-4 pt-3" data-testid="nba-series-live-content">
                                {liveSeries.length > 0 ? (
                                    Object.entries(liveByRound).map(([roundKey, group]) => (
                                        <div key={roundKey} className="space-y-3">
                                            <p className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{group.label}</p>
                                            {group.series.map(renderSeriesCard)}
                                        </div>
                                    ))
                                ) : (
                                    <p data-testid="nba-series-live-empty-notice" className="text-xs text-zinc-500">
                                        Nothing locked or in progress right now — series move here once their Game 1 starts.
                                    </p>
                                )}
                            </CollapsibleContent>
                        </Collapsible>

                        <Collapsible open={showCompleted} onOpenChange={setShowCompleted}>
                            <CollapsibleTrigger
                                data-testid="nba-series-completed-toggle"
                                className="flex items-center justify-between w-full py-2 border-t-2 border-neutral-800 text-left"
                            >
                                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5">
                                    <Trophy className="w-3 h-3" /> Completed ({completedSeries.length})
                                </span>
                                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showCompleted ? "rotate-180" : ""}`} />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-4 pt-3" data-testid="nba-series-completed-content">
                                {completedSeries.length > 0 ? (
                                    Object.entries(completedByRound).map(([roundKey, group]) => (
                                        <div key={roundKey} className="space-y-3">
                                            <p className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{group.label}</p>
                                            {group.series.map(renderSeriesCard)}
                                        </div>
                                    ))
                                ) : (
                                    <p data-testid="nba-series-completed-empty-notice" className="text-xs text-zinc-500">
                                        No series have finished yet — check back once one wraps up.
                                    </p>
                                )}
                            </CollapsibleContent>
                        </Collapsible>
                    </div>
                )}
            </div>
            {!notSetYet && (
                <div className="sticky bottom-0 z-10 mt-4 flex items-center gap-2 bg-[#121212] border-t-2 border-neutral-800 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
                    <button
                        type="button"
                        onClick={onClearAll}
                        disabled={clearing || savingAll}
                        data-testid="nba-series-clear-all-btn"
                        className="brutal-btn-ghost flex-1 py-2.5 text-xs font-bold uppercase tracking-widest disabled:opacity-40"
                    >
                        {clearing ? "Clearing…" : "Clear All Picks"}
                    </button>
                    <button
                        type="button"
                        onClick={onSavePicks}
                        disabled={savingAll || clearing || incompleteCount > 0}
                        data-testid="nba-series-save-picks-btn"
                        title={incompleteCount > 0 ? `Pick a team and a game count for every series first — ${incompleteCount} incomplete` : undefined}
                        className="brutal-btn flex-1 py-2.5 text-xs font-bold uppercase tracking-widest bg-volt text-black border-volt disabled:opacity-40"
                    >
                        {savingAll ? "Saving…" : "Save Picks"}
                    </button>
                </div>
            )}
        </BentoModal>
    );
}
